import React, { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Box, Typography } from '@material-ui/core';
import { useLocalization } from 'Hooks';
import { Container, DashboardLayout, DialogWithActions, LocationIcon } from 'Components';
import { 
  storeAccount, 
  onUpdateCustomerSecurityImageRequest,
  clearSecurityImageError
} from 'Stores/Account';
import { userStore } from 'Stores/User';
import { StyledButton } from './styles';
import { useHistory } from 'react-router-dom';

// Import security images
import securityImage1 from 'Assets/Images/securityImage/1.png';
import securityImage2 from 'Assets/Images/securityImage/2.png';
import securityImage3 from 'Assets/Images/securityImage/3.png';
import securityImage4 from 'Assets/Images/securityImage/4.png';
import securityImage5 from 'Assets/Images/securityImage/5.png';
import securityImage6 from 'Assets/Images/securityImage/6.png';
import securityImage7 from 'Assets/Images/securityImage/7.png';
import securityImage8 from 'Assets/Images/securityImage/8.png';

import './SecurityImageSettings.scss';

const securityImagesBase = [
  { id: 1, src: securityImage1 },
  { id: 2, src: securityImage2 },
  { id: 3, src: securityImage3 },
  { id: 4, src: securityImage4 },
  { id: 5, src: securityImage5 },
  { id: 6, src: securityImage6 },
  { id: 7, src: securityImage7 },
  { id: 8, src: securityImage8 },
];

const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

type SecurityImageSettingsProps = {};

const SecurityImageSettings: FunctionComponent<SecurityImageSettingsProps> = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { sessionToken, customerNumber } = useSelector(userStore);
  const { customerInfo, securityImageUpdateError } = useSelector(storeAccount);
  const localization = useLocalization();
  
  const currentSecurityImageId = customerInfo?.data?.SecurityImageId || 0;
  const [selectedImageId, setSelectedImageId] = useState<number>(currentSecurityImageId);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [previousSecurityImageId, setPreviousSecurityImageId] = useState<number>(currentSecurityImageId);
  const [updateTimeout, setUpdateTimeout] = useState<NodeJS.Timeout | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const securityImages = useMemo(() => shuffleArray(securityImagesBase), []);

  // Sayfa mount olduğunda error state'ini temizle
  useEffect(() => {
    dispatch(clearSecurityImageError());
  }, [dispatch]);

  useEffect(() => {
    if (currentSecurityImageId > 0) {
      setSelectedImageId(currentSecurityImageId);
      
      // SADECE güncelleme yapıldıysa ve SecurityImageId değiştiyse success dialog göster
      if (isUpdating && previousSecurityImageId !== currentSecurityImageId && previousSecurityImageId > 0) {
        if (updateTimeout) clearTimeout(updateTimeout);
        setShowSuccessDialog(true);
        setIsUpdating(false);
        setPreviousSecurityImageId(currentSecurityImageId);
      }
    }
  }, [currentSecurityImageId, isUpdating, previousSecurityImageId, updateTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeout) clearTimeout(updateTimeout);
    };
  }, [updateTimeout]);

  // Store'dan gelen error mesajını dinle
  useEffect(() => {
    if (securityImageUpdateError && isUpdating) {
      if (updateTimeout) clearTimeout(updateTimeout);
      setErrorMessage(securityImageUpdateError);
      setShowErrorDialog(true);
      setIsUpdating(false);
    }
  }, [securityImageUpdateError, isUpdating, updateTimeout]);

  const handleImageClick = (imageId: number) => {
    setSelectedImageId(imageId);
  };

  const handleConfirm = () => {
    if (selectedImageId && sessionToken && customerNumber) {
      setIsUpdating(true);
      setPreviousSecurityImageId(currentSecurityImageId);
      
      // Timeout: 10 saniye içinde güncelleme olmazsa hata göster
      const timeout = setTimeout(() => {
        if (isUpdating) {
          setIsUpdating(false);
          setErrorMessage('İşlem zaman aşımına uğradı. Lütfen tekrar deneyin.');
          setShowErrorDialog(true);
        }
      }, 10000);
      
      setUpdateTimeout(timeout);
      
      dispatch(
        onUpdateCustomerSecurityImageRequest({
          CustomerNumber: String(customerNumber),
          SessionToken: sessionToken,
          SecurityImageId: selectedImageId,
        })
      );
    }
  };

  return (
    <DashboardLayout backButton>
      <Container>
        <div className="security-image-settings">
          <Box className="security-image-settings__header">
            <Typography variant="h5" component="h1" className="security-image-settings__title">
              {localization?.web_security_image_header || 'Güvenlik Resmi Seçiniz'}
            </Typography>
          </Box>

          <Box className="security-image-settings__images-wrapper">
            <Typography variant="body2" component="p" color="textSecondary" className="security-image-settings__description">
               {localization?.web_security_image_description || 'Hesabınızı korumak için bir güvenlik resmi seçin. Bu resim, giriş yaparken size gösterilecektir.'}
            </Typography>
            
            <Grid container spacing={4} className="security-image-settings__grid">
              {securityImages.map((image) => (
                <Grid item xs={6} sm={3} key={image.id}>
                  <Box
                    onClick={() => handleImageClick(image.id)}
                    tabIndex={0}
                    onKeyDown={(e)=>{
                      if(e.key==='Enter'||  e.key===' '){
                        e.preventDefault();
                        handleImageClick(image.id);
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      border: selectedImageId === image.id ? '3px solid #55d3ff' : '2px solid #e0e0e0',
                      borderRadius: '8px',
                      padding: '4px',
                      transition: 'all 0.3s ease',
                      width: '100px',
                      height: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                    }}
                    className={selectedImageId === image.id ? 'security-image-box selected' : 'security-image-box'}
                  >
                    <img
                      src={image.src}
                      alt={`Security ${image.id}`}
                      style={{
                        width: '90px',
                        height: '90px',
                        display: 'block',
                        borderRadius: '4px',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box className="security-image-settings__button-container">
            <StyledButton
              variant="contained"
              onClick={handleConfirm}
              disabled={!selectedImageId || selectedImageId === currentSecurityImageId}
              className="primary-button"
            >
              Onayla
            </StyledButton>
          </Box>
        </div>

        <DialogWithActions
          alignTitle="left"
          primaryButton={{
            label: 'Tamam',
            handleClick: () => {
              setShowSuccessDialog(false);
              history.goBack();
            },
          }}
          open={showSuccessDialog}
          hide={() => {
            setShowSuccessDialog(false);
            history.goBack();
          }}
          title={localization?.web_btn_success || 'Başarılı'}
          text={localization?.web_successMessage || 'Güvenlik resminiz başarıyla güncellendi.'}
        />

        <DialogWithActions
          alignTitle="left"
          primaryButton={{
            label: localization.web_btn_okay || 'Tamam',
            handleClick: () => {
              setShowErrorDialog(false);
              dispatch(clearSecurityImageError());
            },
          }}
          open={showErrorDialog}
          hide={() => {
            setShowErrorDialog(false);
            dispatch(clearSecurityImageError());
          }}
          title={localization.web_fail_title || 'Hata'}
          text={localization[9004] || 'Güvenlik resmi güncellenirken bir hata oluştu. Lütfen tekrar deneyin.'}
        />
      </Container>
    </DashboardLayout>
  );
};

export default SecurityImageSettings;
